import React, { useMemo, useState, useEffect } from 'react';
import MapboxMap, { Marker, Popup, Source, Layer } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { DEFAULT_MAP_CONFIG, handleMapboxError, useMapboxToken } from '../utils/mapbox';
import { circlePolygon, zoomForRadiusMiles, radiusMilesToPixels } from '../utils/mapCircle';

interface MapboxMapWrapperProps {
  center: [number, number]; // [latitude, longitude]
  zoom?: number;
  markers?: Array<{
    position: [number, number]; // [latitude, longitude]
    content: React.ReactNode;
    popup?: React.ReactNode;
    /** Mapbox marker anchor. Default "bottom" (pins). Use "center" for dots. */
    anchor?: 'center' | 'top' | 'bottom' | 'left' | 'right';
  }>;
  route?: {
    coordinates: [number, number][]; // Array of [latitude, longitude] pairs
    color?: string;
    width?: number;
  };
  /** Delivery radius in miles centered on `center` (green shade + dashed border). */
  radiusMiles?: number | null;
  height?: string;
  scrollZoom?: boolean;
  /** When false, map is view-only (no pan/zoom). Default true. */
  interactive?: boolean;
  onLoad?: () => void;
  onError?: (error: any) => void;
  className?: string;
  style?: React.CSSProperties;
}

const RADIUS_FILL = 'rgba(34, 197, 94, 0.26)';
const RADIUS_STROKE = '#15803d';

function parseCssHeightPx(height: string | undefined, fallback: number): number {
  if (!height) return fallback;
  const m = String(height).match(/^(\d+(?:\.\d+)?)px$/i);
  if (m) return Math.max(120, Math.round(Number(m[1])));
  return fallback;
}

function staticMapUrl(
  token: string,
  lat: number,
  lng: number,
  zoom: number,
  width: number,
  height: number
): string {
  const w = Math.min(1280, Math.max(200, Math.round(width)));
  const h = Math.min(1280, Math.max(120, Math.round(height)));
  const z = Math.max(0, Math.min(22, Number(zoom.toFixed(2))));
  return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/${lng.toFixed(5)},${lat.toFixed(5)},${z},0/${w}x${h}@2x?access_token=${encodeURIComponent(token)}&attribution=false&logo=false`;
}

const MapboxMapWrapper: React.FC<MapboxMapWrapperProps> = ({
  center,
  zoom = DEFAULT_MAP_CONFIG.zoom,
  markers = [],
  route,
  radiusMiles = null,
  height = '100%',
  scrollZoom = true,
  interactive = true,
  onLoad,
  onError,
  className = '',
  style = {},
}) => {
  const { token: mapboxToken, loading: tokenLoading, valid: tokenValid } = useMapboxToken();
  const [mapError, setMapError] = useState<string | null>(null);
  const [openPopups, setOpenPopups] = useState<Set<number>>(new Set());
  const [mapReady, setMapReady] = useState(false);
  const [staticWidth, setStaticWidth] = useState(640);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const centerLat = center[0];
  const centerLng = center[1];
  const cssHeightPx = parseCssHeightPx(typeof height === 'string' ? height : undefined, 320);

  const effectiveZoom = useMemo(() => {
    if (radiusMiles != null && radiusMiles > 0) {
      return zoomForRadiusMiles(centerLat, radiusMiles);
    }
    return zoom;
  }, [centerLat, radiusMiles, zoom]);

  const [viewState, setViewState] = useState({
    longitude: centerLng,
    latitude: centerLat,
    zoom: effectiveZoom,
  });

  useEffect(() => {
    setViewState({
      longitude: centerLng,
      latitude: centerLat,
      zoom: effectiveZoom,
    });
    setMapReady(false);
  }, [centerLat, centerLng, effectiveZoom]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width || 0);
      if (w > 0) setStaticWidth((prev) => (Math.abs(w - prev) > 24 ? w : prev));
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const radiusGeoJson = useMemo(() => {
    if (radiusMiles == null || radiusMiles <= 0) return null;
    return circlePolygon(centerLat, centerLng, radiusMiles);
  }, [centerLat, centerLng, radiusMiles]);

  const previewUrl = useMemo(() => {
    if (!mapboxToken || !tokenValid) return null;
    return staticMapUrl(mapboxToken, centerLat, centerLng, effectiveZoom, staticWidth, cssHeightPx);
  }, [mapboxToken, tokenValid, centerLat, centerLng, effectiveZoom, staticWidth, cssHeightPx]);

  const previewShadePx = useMemo(() => {
    if (radiusMiles == null || radiusMiles <= 0) return null;
    const px = radiusMilesToPixels(centerLat, radiusMiles, effectiveZoom);
    return px > 4 ? px : null;
  }, [radiusMiles, centerLat, effectiveZoom]);

  const handleError = (e: any) => {
    handleMapboxError(e, setMapError);
    if (onError) onError(e);
  };

  const handleMapLoad = (e: { target?: { resize?: () => void } }) => {
    setMapError(null);
    setMapReady(true);
    requestAnimationFrame(() => {
      e.target?.resize?.();
    });
    if (onLoad) onLoad();
  };

  const togglePopup = (index: number) => {
    setOpenPopups((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  if (tokenLoading && !previewUrl) {
    return (
      <div
        className={`w-full bg-gray-100 border border-gray-200 rounded-lg ${className}`}
        style={{ height, ...style }}
        aria-busy="true"
        aria-label="Loading map"
      />
    );
  }

  if (!tokenValid || !mapboxToken) {
    return (
      <div
        className={`w-full bg-yellow-50 border border-yellow-300 rounded-lg flex items-center justify-center ${className}`}
        style={{ height, ...style }}
      >
        <div className="text-center p-6 max-w-md">
          <p className="text-yellow-800 font-semibold text-lg mb-2">Mapbox token required</p>
          <p className="text-yellow-700 text-sm mb-4">
            To display maps, set a Mapbox public token on the server or in the client build.
          </p>
          <div className="bg-white rounded-lg p-4 text-left text-sm text-gray-700">
            <p className="font-semibold mb-2">Steps to fix:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>
                Go to{' '}
                <a
                  href="https://account.mapbox.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:text-green-700 underline"
                >
                  mapbox.com
                </a>{' '}
                and create a public token
              </li>
              <li>
                Add to <code className="bg-gray-100 px-1 rounded">server/.env</code>:{' '}
                <code className="bg-gray-100 px-1 rounded">MAPBOX_ACCESS_TOKEN=pk...</code>
              </li>
              <li>
                Or add to <code className="bg-gray-100 px-1 rounded">client/.env</code>:{' '}
                <code className="bg-gray-100 px-1 rounded">REACT_APP_MAPBOX_TOKEN=pk...</code>
              </li>
              <li>Restart the dev server</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  const routeGeoJson = route
    ? {
        type: 'Feature' as const,
        geometry: {
          type: 'LineString' as const,
          coordinates: route.coordinates.map(([lat, lng]) => [lng, lat]),
        },
        properties: {},
      }
    : null;

  return (
    <div
      ref={containerRef}
      className={`w-full relative overflow-hidden ${className}`}
      style={{ position: 'relative', width: '100%', height, minHeight: 200, ...style }}
    >
      {previewUrl && !mapReady && (
        <>
          <img
            src={previewUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            decoding="async"
            fetchPriority="high"
          />
          {previewShadePx != null && (
            <div
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
              aria-hidden
            >
              <div
                style={{
                  width: previewShadePx * 2,
                  height: previewShadePx * 2,
                  borderRadius: '50%',
                  backgroundColor: RADIUS_FILL,
                  border: `2px dashed ${RADIUS_STROKE}`,
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}
        </>
      )}
      {mapError && (
        <div className="absolute inset-0 z-50 flex items-center justify-center rounded-lg bg-gray-100 bg-opacity-90">
          <div className="p-4 text-center">
            <p className="mb-2 font-semibold text-red-600">Map failed to load</p>
            <p className="text-sm text-gray-600">{mapError}</p>
          </div>
        </div>
      )}
      <MapboxMap
        mapboxAccessToken={mapboxToken}
        {...viewState}
        onMove={interactive ? (evt) => setViewState(evt.viewState) : undefined}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: mapReady ? 1 : 0,
          transition: 'opacity 160ms ease-out',
        }}
        mapStyle={DEFAULT_MAP_CONFIG.style}
        scrollZoom={interactive && scrollZoom}
        dragPan={interactive}
        dragRotate={false}
        doubleClickZoom={interactive}
        touchZoomRotate={interactive}
        touchPitch={false}
        keyboard={interactive}
        reuseMaps
        onError={handleError}
        onLoad={handleMapLoad}
      >
        {radiusGeoJson && (
          <Source id="delivery-radius" type="geojson" data={radiusGeoJson}>
            <Layer
              id="delivery-radius-fill"
              type="fill"
              paint={{
                'fill-color': RADIUS_FILL,
              }}
            />
            <Layer
              id="delivery-radius-outline"
              type="line"
              paint={{
                'line-color': RADIUS_STROKE,
                'line-width': 2,
                'line-dasharray': [2.5, 2],
                'line-opacity': 1,
              }}
            />
          </Source>
        )}

        {route && routeGeoJson && (
          <Source id="route" type="geojson" data={routeGeoJson}>
            <Layer
              id="route-line"
              type="line"
              layout={{
                'line-join': 'round',
                'line-cap': 'round',
              }}
              paint={{
                'line-color': route.color || '#22C55E',
                'line-width': route.width || 4,
                'line-opacity': 0.6,
                'line-dasharray': [2, 2],
              }}
            />
          </Source>
        )}

        {markers.map((marker, index) => (
          <React.Fragment key={index}>
            <Marker
              longitude={marker.position[1]}
              latitude={marker.position[0]}
              anchor={marker.anchor || 'bottom'}
            >
              <div className="cursor-pointer" onClick={() => marker.popup && togglePopup(index)}>
                {marker.content}
              </div>
            </Marker>
            {marker.popup && openPopups.has(index) && (
              <Popup
                longitude={marker.position[1]}
                latitude={marker.position[0]}
                anchor="bottom"
                onClose={() => togglePopup(index)}
                closeButton={true}
                closeOnClick={false}
              >
                {marker.popup}
              </Popup>
            )}
          </React.Fragment>
        ))}
      </MapboxMap>
    </div>
  );
};

export default MapboxMapWrapper;
